import React, { useState } from 'react';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { usePWAInstallation } from '../hooks/usePWAInstallation';

interface NotificationButtonProps {
  vapidPublicKey?: string;
  serverEndpoint?: string;
  className?: string;
}

const SMORG_TENANT_VAPID_PUBLIC_KEY = 'BGPR3zGmNxiAXFtBV6y1tV8VgyfxtnCgzkt6ASysZ_Gsg3TzC5dlj3n33-05H-Z7dzpnAXh0n9hIrL2wXmmguAw';

export const NotificationButton: React.FC<NotificationButtonProps> = ({
  vapidPublicKey = SMORG_TENANT_VAPID_PUBLIC_KEY,
  serverEndpoint = 'https://smorgbot-poc-api.smorg.io/webpush/subscribe', // Replace with your actual server endpoint
  // serverEndpoint = 'http://localhost:8000/webpush/subscribe', // Replace with your actual server endpoint
  className = ''
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const {
    state,
    requestPermission,
    subscribe,
    unsubscribe,
    sendTestNotification
  } = usePushNotifications(vapidPublicKey, serverEndpoint);

  const {
    canShowInstallButton,
    canShowManualInstallInstructions,
    manualInstallInstructions,
    promptInstall,
    isInstalling
  } = usePWAInstallation();

  console.log('Are we offering to install a PWA? ', canShowInstallButton);
  console.log('Are we currently installing the PWA? ', isInstalling);

  const handleToggleNotifications = async () => {
    if (state.permission === 'default') {
      await requestPermission();
    } else if (state.permission === 'granted' && !state.isSubscribed) {
      await subscribe(vapidPublicKey);
    } else if (state.isSubscribed) {
      await unsubscribe();
    }
  };

  const handleInstallApp = async () => {
    // If we have a prompt, use it
    if (canShowInstallButton) {
      const result = await promptInstall();
      if (result.outcome === 'error') {
        console.error('Failed to install app:', result.error);
      }
    } else if (buttonState.showInstructions) {
      // Show manual instructions - toggle dropdown
      setShowDropdown(!showDropdown);
    }
  };

  const getButtonState = () => {
    // If push notifications are not supported, show install app option
    if (!state.isSupported && canShowInstallButton) {
      return { 
        text: isInstalling ? 'Installing...' : 'Add to Home Screen', 
        disabled: isInstalling, 
        color: 'purple',
        isInstallButton: true,
        showInstructions: false
      };
    }

    // If push notifications are not supported but manual install is possible
    if (!state.isSupported && canShowManualInstallInstructions) {
      return { 
        text: 'Add to Home Screen', 
        disabled: false, 
        color: 'purple',
        isInstallButton: true,
        showInstructions: true
      };
    }
    
    if (!state.isSupported) {
      return { text: 'Not Supported', disabled: true, color: 'gray', isInstallButton: false, showInstructions: false };
    }
    
    if (state.permission === 'denied') {
      return { text: 'Blocked', disabled: true, color: 'red', isInstallButton: false, showInstructions: false };
    }
    
    if (state.isSubscribed) {
      return { text: 'Notifications On', disabled: false, color: 'green', isInstallButton: false, showInstructions: false };
    }
    
    if (state.permission === 'granted') {
      return { text: 'Enable Notifications', disabled: false, color: 'blue', isInstallButton: false, showInstructions: false };
    }
    
    return { text: 'Allow Notifications', disabled: false, color: 'blue', isInstallButton: false, showInstructions: false };
  };

  const buttonState = getButtonState();

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={buttonState.isInstallButton ? handleInstallApp : () => setShowDropdown(!showDropdown)}
        className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          buttonState.color === 'green' 
            ? 'bg-green-100 text-green-800 hover:bg-green-200' 
            : buttonState.color === 'red'
            ? 'bg-red-100 text-red-800'
            : buttonState.color === 'gray'
            ? 'bg-gray-100 text-gray-600'
            : buttonState.color === 'purple'
            ? 'bg-purple-100 text-purple-800 hover:bg-purple-200'
            : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
        } ${buttonState.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
        disabled={buttonState.disabled}
      >
        {/* Icon - Bell for notifications, Download for install */}
        {buttonState.isInstallButton ? (
          <svg 
            className="w-4 h-4" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
            />
          </svg>
        ) : (
          <svg 
            className="w-4 h-4" 
            fill={state.isSubscribed ? 'currentColor' : 'none'} 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M15 17h5l-3.5-3.5A2.982 2.982 0 0117 12V9c0-3.5-2.5-6-6-6S5 5.5 5 9v3c0 .55-.2 1.05-.5 1.5L1 17h5m4 0v1a3 3 0 01-6 0v-1m6 0H9" 
            />
            {state.isSubscribed && (
              <circle cx="18" cy="6" r="3" fill="currentColor" />
            )}
          </svg>
        )}
        <span className="hidden sm:inline">{buttonState.text}</span>
        
        {/* Dropdown Arrow - only show for notification button */}
        {!buttonState.isInstallButton && (
          <svg 
            className={`w-3 h-3 transition-transform ${showDropdown ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>

      {/* Dropdown Menu - show for notification button or manual install instructions */}
      {showDropdown && (
        <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="p-4">
            {buttonState.isInstallButton && buttonState.showInstructions ? (
              // Manual installation instructions
              <>
                <h3 className="text-sm font-medium text-gray-900 mb-3">Add to Home Screen</h3>
                <div className="mb-4">
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {manualInstallInstructions}
                  </p>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    Add this app to your home screen for quick access and a better experience.
                  </p>
                </div>
              </>
            ) : (
              // Notification settings
              <>
                <h3 className="text-sm font-medium text-gray-900 mb-3">Push Notifications</h3>
                
                {/* Status */}
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Permission:</span>
                    <span className={`font-medium ${
                      state.permission === 'granted' ? 'text-green-600' : 
                      state.permission === 'denied' ? 'text-red-600' : 'text-yellow-600'
                    }`}>
                      {state.permission === 'granted' ? 'Granted' : 
                       state.permission === 'denied' ? 'Denied' : 'Not requested'}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Status:</span>
                    <span className={`font-medium ${state.isSubscribed ? 'text-green-600' : 'text-gray-600'}`}>
                      {state.isSubscribed ? 'Subscribed' : 'Not subscribed'}
                    </span>
                  </div>
                </div>

                {/* Error */}
                {state.error && (
                  <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                    {state.error}
                  </div>
                )}

                {/* Actions */}
                <div className="space-y-2">
                  {state.permission !== 'denied' && (
                    <button
                      onClick={handleToggleNotifications}
                      disabled={state.isLoading || !state.isSupported}
                      className={`w-full px-3 py-2 text-xs rounded ${
                        state.isSubscribed 
                          ? 'bg-red-600 text-white hover:bg-red-700' 
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      } disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                    >
                      {state.isLoading ? 'Loading...' : 
                       state.isSubscribed ? 'Turn Off' : 'Turn On'}
                    </button>
                  )}
                  
                  {state.isSubscribed && (
                    <button
                      onClick={() => {
                        sendTestNotification({
                          title: 'Test Notification',
                          body: 'Your notifications are working!',
                          icon: '/favicon.ico'
                        });
                        setShowDropdown(false);
                      }}
                      className="w-full px-3 py-2 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Send Test
                    </button>
                  )}
                </div>

                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    Get notified about new messages and updates.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* Overlay to close dropdown */}
      {showDropdown && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};